import * as React from 'react';
import type { QuizAnswer, QuizGameState, QuizQuestion } from './types';

export interface UseQuizGameOptions {
  enableSound?: boolean;
}

function evaluateAnswer(question: QuizQuestion, ids: string[]): boolean {
  const correctSet = new Set(question.correctIds);
  const selectedSet = new Set(ids);
  if (correctSet.size !== selectedSet.size) return false;
  return [...selectedSet].every((id) => correctSet.has(id));
}

export function useQuizGame(questions: QuizQuestion[], _options: UseQuizGameOptions = {}) {
  const [gameState, setGameState] = React.useState<QuizGameState>('start');
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const selectedIdsRef = React.useRef<string[]>([]);
  React.useEffect(() => {
    selectedIdsRef.current = selectedIds;
  }, [selectedIds]);
  const [answers, setAnswers] = React.useState<QuizAnswer[]>([]);
  const gameStateRef = React.useRef(gameState);
  React.useEffect(() => {
    gameStateRef.current = gameState;
  }, [gameState]);

  const currentQuestion = questions[currentIndex];
  const isMultiple = currentQuestion?.type === 'multiple';
  const totalQuestions = questions.length;
  const maxScore = questions.reduce((sum, q) => sum + (q.score ?? 0), 0);
  const score = answers.reduce((sum, a) => sum + (a.isCorrect ? a.scoreAwarded : 0), 0);
  const progressPercent =
    totalQuestions === 0 ? 0 : Math.min(100, ((currentIndex + 1) / totalQuestions) * 100);

  const resetSelections = React.useCallback(() => setSelectedIds([]), []);

  const submitAnswer = React.useCallback(
    (finalIds: string[]) => {
      if (gameStateRef.current === 'answered') return;
      const q = questions[currentIndex];
      if (!q) return;
      const isCorrect = evaluateAnswer(q, finalIds);
      setAnswers((prev) => [
        ...prev,
        {
          questionId: q.id,
          selectedIds: finalIds,
          isCorrect,
          scoreAwarded: isCorrect ? q.score : 0,
        },
      ]);
      setGameState('answered');
    },
    [currentIndex, questions]
  );

  const handleOptionToggle = React.useCallback(
    (optionId: string) => {
      if (gameStateRef.current === 'answered') return;
      const q = questions[currentIndex];
      if (!q) return;
      if (q.type !== 'multiple') {
        setSelectedIds([optionId]);
        submitAnswer([optionId]);
        return;
      }
      setSelectedIds((prev) => {
        const exists = prev.includes(optionId);
        return exists ? prev.filter((id) => id !== optionId) : [...prev, optionId];
      });
    },
    [currentIndex, questions, submitAnswer]
  );

  const submitCurrentMultiple = React.useCallback(() => {
    submitAnswer([...selectedIdsRef.current]);
  }, [submitAnswer]);

  const goNext = React.useCallback(() => {
    if (currentIndex < totalQuestions - 1) {
      setCurrentIndex((i) => i + 1);
      setGameState('idle');
      resetSelections();
    } else {
      setGameState('finished');
    }
  }, [currentIndex, totalQuestions, resetSelections]);

  const startGame = React.useCallback(() => {
    if (!totalQuestions) return;
    setGameState('idle');
    setCurrentIndex(0);
    setAnswers([]);
    resetSelections();
  }, [totalQuestions, resetSelections]);

  const restart = React.useCallback(() => {
    setGameState('start');
    setCurrentIndex(0);
    setAnswers([]);
    resetSelections();
  }, [resetSelections]);

  React.useEffect(() => {
    restart();
  }, [questions.length, restart]);

  return {
    gameState,
    currentIndex,
    currentQuestion,
    isMultiple,
    selectedIds,
    answers,
    score,
    totalQuestions,
    maxScore,
    progressPercent,
    startGame,
    restart,
    submitAnswer,
    submitCurrentMultiple,
    handleOptionToggle,
    goNext,
    resetSelections,
  };
}
